import React, { useEffect, useRef, useState } from "react";
import { FaComments, FaMicrophone, FaMicrophoneSlash, FaPaperPlane, FaTimes, FaVolumeMute, FaVolumeUp } from "react-icons/fa";
import { profile } from "../data/profile";

const SpeechRecognition =
  typeof window !== "undefined"
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

const WELCOME =
  `Hi! I'm Kalgi's portfolio assistant. Ask about her experience, projects, skills, or how to get in touch by voice or chat.`;

function localFallbackReply(userText) {
  const text = (userText || "").toLowerCase();
  if (text.includes("email") || text.includes("contact") || text.includes("reach") || text.includes("hire") || text.includes("opportunity")) {
    return `Yes, Kalgi is interested in software engineering roles in backend, cloud, data, and AI. Reach her at ${profile.contact.email} or book ${profile.contact.calendly}.`;
  }
  if (text.includes("based") || text.includes("live") || text.includes("location") || text.includes("where")) {
    return `Currently, She is based in California`;
  }
  if (text.includes("experience") || text.includes("work") || text.includes("infor") || text.includes("job")) {
    return `Kalgi most recently worked as a Software Developer at Arizona State University and previously as an Associate Software Engineer at Infor, focusing on scalable backends, APIs, and cloud systems.`;
  }
  if (text.includes("educat") || text.includes("degree") || text.includes("university") || text.includes("school")) {
    return `Kalgi completed her MS in Computer Science from Arizona State University (2024–2026) and earned a B.Tech in Computer Engineering from Pandit Deendayal Energy University.`;
  }
  if (text.includes("skill") || text.includes("tech") || text.includes("stack") || text.includes("good at") || text.includes("specialize")) {
    return `Kalgi specializes in scalable backends, APIs, cloud (especially AWS), data systems, and AI applications such as RAG chatbots and LLM agents.`;
  }
  return `I'm Kalgi's portfolio assistant. Ask about her experience, skills, projects, or hiring. You can email her on ${profile.contact.email} or book ${profile.contact.calendly}.`;
}

function fallbackStatusMessage(data) {
  if (!data?.fallback) return "";
  if (data.reason === "missing_api_key") {
    return "Gemini key missing, add GEMINI_API_KEY to .env (local) or Vercel env vars.";
  }
  if (data.reason === "rate_limited") {
    return "Free Gemini quota busy, showing a short fallback answer.";
  }
  if (data.reason === "upstream_error") {
    return data.detail
      ? `Gemini error - ${data.detail}`
      : "Gemini request failed - showing a short fallback answer.";
  }
  return data.detail || "Used a local fallback answer.";
}

function VoiceAssistant() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    { role: "assistant", content: WELCOME },
  ]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [listening, setListening] = useState(false);
  const [speechEnabled, setSpeechEnabled] = useState(true);
  const [speechSupported] = useState(Boolean(SpeechRecognition));
  const recognitionRef = useRef(null);
  const bottomRef = useRef(null);
  const speakEnabledRef = useRef(true);

  useEffect(() => {
    speakEnabledRef.current = speechEnabled;
  }, [speechEnabled]);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("open-voice-assistant", onOpen);
    return () => window.removeEventListener("open-voice-assistant", onOpen);
  }, []);

  useEffect(() => {
    if (open && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open, loading]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  function speak(text) {
    if (!speakEnabledRef.current || !text || typeof window === "undefined") return;
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.02;
    utterance.pitch = 1;
    window.speechSynthesis.speak(utterance);
  }

  async function sendMessage(rawText) {
    const text = (rawText || "").trim();
    if (!text || loading) return;

    setError("");
    setStatus("");
    setInput("");
    const nextMessages = [...messages, { role: "user", content: text }];
    setMessages(nextMessages);
    setLoading(true);

    try {
      const res = await fetch("/api/assistant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: nextMessages
            .filter((m) => m.role === "user" || m.role === "assistant")
            .map((m) => ({ role: m.role, content: m.content })),
        }),
      });

      if (!res.ok) {
        throw new Error("Assistant request failed");
      }

      const data = await res.json();
      const reply = data.reply || localFallbackReply(text);

      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
      if (data.fallback) {
        setStatus(fallbackStatusMessage(data));
      } else {
        setStatus("");
      }
      speak(reply);
    } catch (err) {
      const fallback = localFallbackReply(text);
      setMessages((prev) => [...prev, { role: "assistant", content: fallback }]);
      setStatus(
        "Could not reach /api/assistant. Start it with: node scripts/local-api.js"
      );
      speak(fallback);
    } finally {
      setLoading(false);
    }
  }

  function toggleListening() {
    if (!speechSupported) {
      setError("Voice input works best in Chrome or Edge. You can still type.");
      return;
    }

    if (listening && recognitionRef.current) {
      recognitionRef.current.stop();
      setListening(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setListening(true);
      setStatus("Listening…");
      setError("");
    };

    recognition.onerror = () => {
      setListening(false);
      setStatus("");
      setError("Couldn't capture audio. Check mic permissions or type instead.");
    };

    recognition.onend = () => {
      setListening(false);
      setStatus("");
    };

    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript;
      if (transcript) {
        sendMessage(transcript);
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
  }

  function handleSubmit(e) {
    e.preventDefault();
    sendMessage(input);
  }

  return (
    <>
      {!open ? (
        <button
          type="button"
          className="va-launcher"
          aria-label="Open voice assistant"
          onClick={() => setOpen(true)}
        >
          <FaComments />
        </button>
      ) : (
        <div className="va-panel" role="dialog" aria-label="Portfolio voice assistant">
          <div className="va-header">
            <div>
              <h2>Ask about Kalgi</h2>
            </div>
            <div className="va-header-actions">
              <button
                type="button"
                className={`va-icon-btn ${speechEnabled ? "is-active" : ""}`}
                aria-label={speechEnabled ? "Mute spoken replies" : "Unmute spoken replies"}
                onClick={() => {
                  setSpeechEnabled((v) => !v);
                  if (window.speechSynthesis) window.speechSynthesis.cancel();
                }}
              >
                {speechEnabled ? <FaVolumeUp /> : <FaVolumeMute />}
              </button>
              <button
                type="button"
                className="va-icon-btn"
                aria-label="Close assistant"
                onClick={() => {
                  setOpen(false);
                  if (recognitionRef.current) recognitionRef.current.stop();
                  if (window.speechSynthesis) window.speechSynthesis.cancel();
                }}
              >
                <FaTimes />
              </button>
            </div>
          </div>

          <div className="va-messages">
            {messages.map((msg, idx) => (
              <div
                key={`${msg.role}-${idx}-${msg.content.slice(0, 12)}`}
                className={`va-msg va-msg--${msg.role}`}
              >
                {msg.content}
              </div>
            ))}
            {loading ? <div className="va-msg va-msg--assistant">Thinking…</div> : null}
            <div ref={bottomRef} />
          </div>

          {status ? <p className="va-status">{status}</p> : null}
          {error ? <p className="va-status is-error">{error}</p> : null}

          <form className="va-composer" onSubmit={handleSubmit}>
            <button
              type="button"
              className={`va-mic ${listening ? "is-listening" : ""}`}
              aria-label={listening ? "Stop listening" : "Start voice input"}
              onClick={toggleListening}
              disabled={loading}
            >
              {listening ? <FaMicrophoneSlash /> : <FaMicrophone />}
            </button>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={speechSupported ? "Ask or tap the mic…" : "Ask about Kalgi…"}
              aria-label="Message"
              disabled={loading}
            />
            <button type="submit" disabled={loading || !input.trim()} aria-label="Send">
              <FaPaperPlane />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

export default VoiceAssistant;
